import type { ChipIntent } from '@/components/common/chip';
import { formatInAppTz } from '@/lib/date';

import type { ClaimStatus, WarrantyActivityStatus, WarrantyReason, WarrantyStatus } from './types';

/** Order Date & Time: the UTC wire instant on the application (Eastern) clock (matches Orders). */
export function formatWarrantyDateTime(iso: string): string {
  return formatInAppTz(iso, 'D MMM YYYY | hh:mm A');
}

/** Warranty Claim Date: the Eastern day of the claim's `createdAt` instant — the day the Claim Date
 *  filter matches it on (`startOfDayInEasternTime` over `wc.createdAt`). The earlier `slice(0, 10)`
 *  took the **UTC** day, which is a day late for any claim raised in the last hours of an Eastern
 *  evening (ADO 120986). */
export function formatWarrantyClaimDate(value: string): string {
  return formatInAppTz(value, 'D MMM YYYY');
}

/** Claim Status chip (grilling Q3; Figma leaf 612:340517 + frame 30-420359): Pending = yellow
 *  `review`, Approved = green `approved`, Rejected = red `attention`. */
export const CLAIM_STATUS_CHIP: Record<ClaimStatus, { label: string; intent: ChipIntent }> = {
  pending: { label: 'Pending', intent: 'review' },
  approved: { label: 'Approved', intent: 'approved' },
  rejected: { label: 'Rejected', intent: 'attention' },
};

/** Warranty Status chip (Figma frame 30-420359): Active = green, Expired = red. This is the
 *  *device's* coverage state — a separate column and a separate enum from Claim Status. The wire
 *  token is `expired`, not the `inactive` this originally guessed at — the backend computes coverage
 *  from the order's `deliveredAt` plus the item's `warrantyMonths`, and there is no `inactive` state.
 *  The label follows the token ("Expired" says what the state is); CONTEXT.md is updated to match. */
export const WARRANTY_STATUS_CHIP: Record<WarrantyStatus, { label: string; intent: ChipIntent }> = {
  active: { label: 'Active', intent: 'approved' },
  expired: { label: 'Expired', intent: 'attention' },
};

/** The five fixed reasons → display labels (CONTEXT.md). Rendered as plain text, never a chip
 *  (grilling Q4), so the wire enum never leaks to the UI. */
export const WARRANTY_REASONS: Record<WarrantyReason, string> = {
  mechanical_or_electrical_failure: 'Mechanical or Electrical Failure',
  manufacturing_defects: 'Manufacturing Defects',
  performance_failure: 'Performance Failure',
  structural_flaws: 'Structural Flaws',
  others: 'Others',
};

/** Activity Timeline chip per event (Figma 429:301744 / 429:301747). `created` is blue
 *  (`published` = Info/100 + Info/700); every other event *is* a claim status, so the three header
 *  chips are reused verbatim rather than restated. */
export const ACTIVITY_CHIP: Record<WarrantyActivityStatus, { label: string; intent: ChipIntent }> =
  {
    created: { label: 'Created', intent: 'published' },
    ...CLAIM_STATUS_CHIP,
  };
